<template>
  <div class="admin-gradebook-container">
    <div class="grid-container" :style="$global.dynamicCols(5)">
      <h1>All Students</h1>
      <HiloSelect
        v-for="item in fields"
        :key="item.model"
        :options="options[item.model] || []"
        style="width: 100%; height: 40px"
        :searchable="true"
        v-model="filter[item.model]"
        :placeholder="item.placeholder"
        class="grey-theme-input"
      />
      <input
        v-model="filter.search"
        class="admin-gradebook-search"
        type="search"
        name="search"
        id="search"
        placeholder="Search..."
      />
    </div>

    <div class="admin-gradebook">
      <table class="admin-gradebook-table">
        <thead>
          <tr>
            <th><div></div></th>
            <th v-for="header in tableHeaders" :key="header">{{ header }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="student in filteredStudents" :key="student.id">
            <td>
              <img
                v-if="student.user.image || student.user.errorImage"
                @error="() => (student.user.errorImage = true)"
                :src="student.user.image"
                alt=""
              />
            </td>
            <td>{{ $global.getFullName(student.user) }}</td>
            <td>
              {{
                student.details.class && student.details?.averageGrade != null
                  ? `${student.details.averageGrade.toFixed(0)}%`
                  : ""
              }}
            </td>

            <td>
              {{
                student.details.daysMargin != null
                  ? student.details.daysMargin +
                    (" days " +
                      (student.details.daysMargin >= 0 ? "ahead" : "behind"))
                  : ""
              }}
            </td>
            <td>
              {{ student.details.class?.curriculum?.program?.name ?? "" }}
            </td>
            <td>
              {{ student.details.class?.startDate.format("MM/DD/YYYY") ?? "" }}
            </td>
            <td>
              {{ student.details.class?.endDate.format("MM/DD/YYYY") ?? "" }}
            </td>
            <td>{{ $global.formatCurrency(student.balance || 0) }}</td>
            <td class="text-center">
              <img
                class="linkIcon"
                src="/static/img/attachment.svg"
                alt=""
                @click="goToProfile(student.id)"
              />
            </td>
            <td class="text-center">
              <img
                class="linkIcon"
                src="/static/img/attachment.svg"
                alt=""
                @click="goToGradebook(student.userId)"
                v-if="student.details.class"
              />
            </td>
            <td class="text-center">
              <img
                class="linkIcon"
                src="/static/img/attachment.svg"
                alt=""
                @click="goToLogbook(student.userId)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="loading">
        <b-spinner
          label="Loading..."
          variant="primary"
          class="spinner"
        ></b-spinner>
      </div>
      <div
        v-else-if="!filteredStudents.length"
        class="text-center mt-3 text-black-50"
      >
        No data
      </div>
    </div>

    <ToastNotifier ref="toast" />
  </div>
</template>
<script>
import { getProcessedImageUrl } from "@/util/images";
import dayjs from "dayjs";
import ToastNotifier from "../../../../components/ToastNotifier.vue";
import ActiveStudentsAPIService from "../../../../servicehandlers/ActiveStudentsAPIService.js";
import ClassesAPIService from "../../../../servicehandlers/ClassesAPIService.js";
import HiloSelect from "../../../../components/HiloComponents/HiloSelect.vue";
import {
  ADMIN_GRADEBOOK_TABLE_HEADERS as tableHeaders,
  PROGRAMS,
} from "../../../../util/constants.js";

const studentAPIService = new ActiveStudentsAPIService();
const classAPIService = new ClassesAPIService();
export default {
  components: { ToastNotifier, HiloSelect },
  data() {
    return {
      loading: false,
      students: [],
      options: {
        grades: Array.from({ length: 10 }, (_, i) => {
          const start = i * 10;
          const end = start + 10;
          return { id: `${start}-${end}`, name: `${start}-${end}` };
        }),
        status: [
          { id: "ahead", name: "Ahead" },
          { id: "behind", name: "Behind" },
          { id: "on time", name: "On Time" },
        ],
        program: PROGRAMS,
      },
      filter: {
        grades: null,
        status: null,
        program: null,
        search: null,
      },
      fields: [
        { placeholder: "Grades", model: "grades" },
        { placeholder: "Status", model: "status" },
        { placeholder: "Program", model: "program" },
      ],
      tableHeaders,
      classes: [],
    };
  },
  computed: {
    filteredStudents() {
      return this.students.filter((student) => {
        const gradeMatches = this.filter.grades
          ? this.getGradeBracket(
              student.details?.averageGrade,
              this.filter.grades,
            )
          : true;

        const statusMatches = this.filter.status
          ? (student.details?.daysMargin || "").toLowerCase() ===
            (this.filter.status || "").toLowerCase()
          : true;

        const programMatches = this.filter.program
          ? student.details?.curriculum?.program?.name === this.filter.program
          : true;

        const searchInput = this.filter.search
          ? this.$global
              .getFullName(student.user)
              .toLowerCase()
              .includes(this.filter.search.toLowerCase())
          : true;

        return gradeMatches && statusMatches && programMatches && searchInput;
      });
    },
  },
  async mounted() {
    try {
      this.loading = true;
      this.students = await studentAPIService.getAllStudentDetails(
        this.$router,
      );

      for (let student of this.students) {
        if (student.details.class) {
          student.details.class.startDate = dayjs(
            student.details.class.startDate,
          );
          student.details.class.endDate = dayjs(student.details.class.endDate);
        }
        student.user.image = getProcessedImageUrl(student.user.image);
      }
    } catch (error) {
      this.$refs.toast.showError(error.message);
    }
    this.loading = false;
  },
  methods: {
    getGradeBracket(grade, range) {
      if (!range) return true;
      if (!grade) return false;

      const [min, max] = range.split("-").map(Number);
      return grade >= min && grade < max;
    },
    goToProfile(id) {
      this.$router.push({
        name: "Profile Student",
        params: {
          id,
        },
      });
    },
    goToGradebook(studentId) {
      this.$router.push({
        name: "Single Student GradeBook",
        query: {
          studentId,
        },
      });
    },
    goToLogbook(userId) {
      this.$router.push({
        name: "Logbook",
        query: {
          userId,
        },
      });
    },
  },
};
</script>
<style>
.loading {
  display: flex;
  justify-content: center;
  padding: 30px;
}
.linkIcon {
  cursor: pointer;
  width: 20px !important;
}
.admin-gradebook-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.grid-container {
  display: grid;
  align-items: center;
  place-items: center;
  gap: 10px;
  box-sizing: border-box;
}
.grid-container h1 {
  font-weight: 600;
  font-size: 25px;
  margin-top: 10px;
}
.admin-gradebook {
  width: 100%;
  display: grid;
}
.admin-gradebook-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.admin-gradebook-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  text-align: left;
}
.admin-gradebook-table th {
  background-color: rgba(60, 120, 255, 1);
  color: white;
  font-weight: 400;
  padding: 5px 10px;
}
.admin-gradebook-table th:first-child {
  border-top-left-radius: 10px;
}
.admin-gradebook-table th:last-child {
  border-top-right-radius: 10px;
}
.admin-gradebook-table th:first-child {
  width: 30px;
}
.admin-gradebook-table th:first-child div {
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
}
.admin-gradebook-table img {
  height: 25px;
  width: 25px;
  border-radius: 5px;
}
.admin-gradebook-search {
  background: transparent !important;
  border: 1px solid #ccc;
  height: 40px;
  width: 100% !important;
}
</style>
